import React, { useEffect, useRef, useState } from 'react'
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import {
  ButtonV2,
  BoxV2,
  CheckBoxField,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { useDispatch, useSelector } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import { AxiosError, AxiosResponse } from 'axios'
import { isEmpty } from 'lodash'
import {
  faExclamationCircle,
  faSquareSliders,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PartnerType } from '../../types/partner'
import { CRMTypes } from '../../utils/constants'
import {
  deleteCRMFromCRMService,
  getCRMAuth,
  patchCRMSchema,
} from '../../api/crm'
import {
  FormData,
  setCRMFormData,
  setDeletedAt,
} from '../../modules/crmAuth/crmConnectionActions'
import { cleanUpCRMTables, cleanUpTableFields } from '../../modules/crmCleanup/actions'
import { updateAppAlert } from '../../modules/appAlert/actions'
import { errorLogger } from '../../utils/errorLogger'
import { getErrorMessages } from '../../utils/error'
import {
  RequestFailureMessage,
  UpdateCRMSchemaSuccessMessage,
  CRMDisconnectFailureMessage,
} from '../../utils/messagesContants'
import { useStyles } from './SalesforceIntegrationTypeCard.styles'
import { selectedCRM } from './CRMSelectorUI'
import { buildCanvasMigrationSchemaPatch } from './buildCanvasMigrationSchemaPatch'
import { getAdminMigrationUiState } from './getAdminMigrationUiState'
import { AdminCanvasMigrationMonitor } from './AdminCanvasMigrationMonitor'
import type { CrmOnboardingRootState } from '../../modules/crmSelector/crmOnboardingState'

type IntegrationMode = 'native' | 'canvas'

const getSelectedCrm = (selected: selectedCRM): string | undefined => {
  if (!selected) {
    return undefined
  }
  if (typeof selected === 'string') {
    return selected
  }
  return selected.crm_type
}

const mapNewPlatformSyncToMode = (
  newPlatformSync?: boolean
): IntegrationMode => (newPlatformSync ? 'canvas' : 'native')

const mapModeToNewPlatformSync = (mode: IntegrationMode): boolean =>
  mode === 'canvas'

export const SalesforceIntegrationTypeCard: React.FC<{
  selectedCRM?: selectedCRM
}> = ({ selectedCRM }) => {
  const { classes } = useStyles()
  const dispatch =
    useDispatch<ThunkDispatch<CrmOnboardingRootState, unknown, AnyAction>>()
  const [integrationMode, setIntegrationMode] = useState<IntegrationMode | ''>(
    ''
  )
  const [savedIntegrationMode, setSavedIntegrationMode] = useState<
    IntegrationMode | ''
  >('')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [customerNeedsMigration, setCustomerNeedsMigration] = useState(true)

  const globalState = useSelector((state: CrmOnboardingRootState) => state)

  const crmId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.[PartnerType.Admin]?.partnerData?.crmId || ''
  )

  const partnerId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.[PartnerType.Admin]?.partnerData?.partnerId || ''
  )

  const crmFormData = useSelector(
    (state: CrmOnboardingRootState) => state.CRMAuth.crmFormData
  )

  const isCrmConnected =
    !isEmpty(crmFormData) &&
    !isEmpty(crmId) &&
    (crmFormData?.creds || []).length > 0 &&
    isEmpty(crmFormData?.deleted_at)

  const partnerCrmType = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.[
        PartnerType.Admin
      ]?.partnerData?.crmType?.toLowerCase() as CRMTypes | undefined
  )

  const isSalesforceSelected =
    getSelectedCrm(selectedCRM) === CRMTypes.salesforce ||
    partnerCrmType === CRMTypes.salesforce

  const initializedCrmIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!crmId || !isSalesforceSelected) {
      setIntegrationMode('')
      setSavedIntegrationMode('')
      initializedCrmIdRef.current = null
      return
    }

    if (initializedCrmIdRef.current === crmId) {
      return
    }

    const isFormDataForCurrentCrm =
      !crmFormData?.crm_id || crmFormData.crm_id === crmId
    const newPlatformSync = crmFormData?.crm_schema?.new_platform_sync

    if (isFormDataForCurrentCrm && newPlatformSync !== undefined) {
      const mode = mapNewPlatformSyncToMode(newPlatformSync)
      setIntegrationMode(mode)
      setSavedIntegrationMode(mode)
      initializedCrmIdRef.current = crmId
      return
    }

    let cancelled = false

    const fetchIntegrationType = async () => {
      setIsLoading(true)
      try {
        const { data } = await getCRMAuth(crmId)
        if (cancelled) {
          return
        }
        const formData = data as FormData
        dispatch(setCRMFormData(formData))
        const mode = mapNewPlatformSyncToMode(
          formData?.crm_schema?.new_platform_sync
        )
        setIntegrationMode(mode)
        setSavedIntegrationMode(mode)
        initializedCrmIdRef.current = crmId
      } catch (error: unknown) {
        if (cancelled) {
          return
        }
        const e = error as AxiosError
        const errorResponse = e?.response as AxiosResponse<ErrorResponse>
        errorLogger({ globalState })(
          error as Error,
          errorResponse?.data?.track_id
        )
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void fetchIntegrationType()

    return () => {
      cancelled = true
    }
  }, [crmId, isSalesforceSelected, crmFormData, dispatch])

  const switchingNativeToCanvas =
    savedIntegrationMode === 'native' && integrationMode === 'canvas'
  const switchingCanvasToNative =
    savedIntegrationMode === 'canvas' && integrationMode === 'native'
  const showMigrationCheckbox = switchingNativeToCanvas

  const handleIntegrationModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextMode = event.target.value as IntegrationMode
    setIntegrationMode(nextMode)
    if (nextMode === 'canvas' && savedIntegrationMode === 'native') {
      setCustomerNeedsMigration(true)
    }
  }

  const performSave = async ({ disconnectFirst = false } = {}) => {
    if (!crmId || !integrationMode || !isSalesforceSelected) {
      return
    }

    setIsSaving(true)
    let disconnectedAt: string | undefined
    try {
      if (disconnectFirst && isCrmConnected) {
        try {
          await deleteCRMFromCRMService(crmId)
          disconnectedAt = new Date().toISOString()
          dispatch(setDeletedAt(disconnectedAt))
          dispatch(cleanUpCRMTables())
          dispatch(cleanUpTableFields())
        } catch (error: unknown) {
          setOpenConfirmModal(false)
          const e = error as AxiosError
          const errorResponse = e?.response as AxiosResponse<ErrorResponse>
          await dispatch(
            updateAppAlert({
              message: CRMDisconnectFailureMessage,
              messageType: 'ERROR',
              autoClose: true,
            })
          )
          errorLogger({ globalState })(
            error as Error,
            errorResponse?.data?.track_id
          )
          return
        }
      }

      const newPlatformSync = mapModeToNewPlatformSync(integrationMode)
      const existingSchema = crmFormData?.crm_schema ?? {}
      const schemaPatch = buildCanvasMigrationSchemaPatch({
        newPlatformSync,
        switchingNativeToCanvas,
        switchingCanvasToNative,
        customerNeedsMigration,
      })

      const updatedSchema = {
        ...existingSchema,
        ...schemaPatch,
      }
      if (switchingCanvasToNative) {
        delete updatedSchema.canvas_migration_status
      }

      await patchCRMSchema(crmId, schemaPatch as Record<string, unknown>)

      if (crmFormData) {
        dispatch(
          setCRMFormData({
            ...crmFormData,
            ...(disconnectedAt
              ? { creds: [], deleted_at: disconnectedAt }
              : {}),
            crm_schema: updatedSchema as FormData['crm_schema'],
          })
        )
      }

      setSavedIntegrationMode(integrationMode)

      if (disconnectFirst) {
        setOpenConfirmModal(false)
      }

      await dispatch(
        updateAppAlert({
          message: UpdateCRMSchemaSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: unknown) {
      if (disconnectFirst) {
        setOpenConfirmModal(false)
      }
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      await dispatch(
        updateAppAlert({
          message: disconnectedAt
            ? 'Old CRM disconnected successfully but we encountered error while switching, please try again'
            : getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveClick = () => {
    if (!crmId || !integrationMode || !isSalesforceSelected) {
      return
    }

    if (isCrmConnected) {
      setOpenConfirmModal(true)
      return
    }

    void performSave()
  }

  const handleConfirmSave = () => {
    void performSave({ disconnectFirst: true })
  }

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false)
  }

  const isSaveDisabled =
    isSaving ||
    isLoading ||
    !integrationMode ||
    !isSalesforceSelected ||
    integrationMode === savedIntegrationMode

  const isDisabled = !isSalesforceSelected || isLoading || isSaving

  const migrationStatus = crmFormData?.crm_schema?.canvas_migration_status
  const { statusLabel: migrationStatusLabel, showMonitor } =
    getAdminMigrationUiState(migrationStatus)
  const showMigrationMonitor =
    showMonitor && Boolean(crmId) && Boolean(partnerId)

  return (
    <>
      <ModalConfirmV2
        title="Update configuration"
        icon={<FontAwesomeIcon icon={faExclamationCircle} />}
        variant={ModalSizeVariants.small}
        description={
          <>
            If you change this setting now, the entire sync will be disrupted
            and the existing connection between CRM and Labra will break.
            <br />
            Are you sure you want to continue?
          </>
        }
        open={openConfirmModal}
        titleAcceptButton="Update"
        onAccept={handleConfirmSave}
        onDecline={handleCloseConfirmModal}
      />
      <BoxV2
        heading="Salesforce integration type"
        style={{
          borderRadius: '0rem 0rem 0.5rem 0.5rem',
        }}
        headingIcon={
          <FontAwesomeIcon
            icon={faSquareSliders}
            style={{ height: '20px', width: '13px' }}
          />
        }
        data-testid="salesforce-integration-type-card"
      >
        <div className={classes.content}>
          <Typography className={classes.cardSubTitle}>
            Select a salesforce integration type
          </Typography>
          <FormControl className={classes.form} size="small">
            <RadioGroup
              row
              value={integrationMode}
              onChange={handleIntegrationModeChange}
            >
              <FormControlLabel
                value="native"
                label="Native Salesforce app"
                className={classes.radioLabel}
                disabled={isDisabled}
                control={
                  <Radio
                    classes={{
                      root: classes.radio,
                      checked: classes.checked,
                    }}
                  />
                }
                data-testid="salesforce-native-radio"
              />
              <FormControlLabel
                value="canvas"
                label="Labra Canvas app"
                className={classes.radioLabel}
                disabled={isDisabled}
                control={
                  <Radio
                    classes={{
                      root: classes.radio,
                      checked: classes.checked,
                    }}
                  />
                }
                data-testid="salesforce-canvas-radio"
              />
            </RadioGroup>
          </FormControl>
          {showMigrationCheckbox ? (
            <div
              className={classes.migrationCheckbox}
              data-testid="salesforce-migration-checkbox"
            >
              <CheckBoxField
                keyName="customerNeedsMigration"
                title="Customer needs to be migrated"
                value={customerNeedsMigration}
                disabled={isDisabled}
                onChangeBox={event =>
                  setCustomerNeedsMigration(event.target.checked)
                }
              />
            </div>
          ) : null}
          <div className={classes.footer}>
            <ButtonV2
              data-testid="salesforce-integration-save"
              onClick={handleSaveClick}
              size="small"
              disabled={isSaveDisabled}
            >
              Save
            </ButtonV2>
          </div>
          {migrationStatusLabel ? (
            <Typography
              className={classes.migrationStatus}
              data-testid="salesforce-migration-status"
            >
              Migration status: {migrationStatusLabel}
            </Typography>
          ) : null}
          {showMigrationMonitor ? (
            <AdminCanvasMigrationMonitor partnerId={partnerId} crmId={crmId} />
          ) : null}
        </div>
      </BoxV2>
    </>
  )
}
